import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useIntl, defineMessages } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useErrorController } from "@natera/platform/lib/hooks";

import {
  ConfigContext,
  ErrorProvider,
  ResetPasswordContext,
} from "@app/provider";
import { Notification, SpinnerView } from "@app/components";
import { useRecaptchaSubmit, RecaptchaTokens } from "@app/hooks";
import {
  default as ResetPasswordForm,
  emailValidationFieldName,
} from "./resetPasswordForm";
import { validateEmail } from "@app/utils";
import { isRecaptchaErrorCode } from "@app/service/resultCodes";
import { routes } from "@app/routing";
import "./resetPassword.scss";

const RECAPTCHA_RESET_PASSWORD_ACTION = "reset_password";

const messages = defineMessages({
  resendPasswordTitle: {
    id: "resendPasswordTitle",
    defaultMessage: "Forgot Password?",
  },
  resendPasswordInstructions: {
    id: "resendPasswordInstructions",
    defaultMessage:
      "No worries. We will send you an email with reset instructions.",
  },
  resendPasswordInvalidEmailError: {
    id: "resendPasswordInvalidEmailError",
    defaultMessage: "Please enter a valid email address. (username@domain)",
  },
  resendPasswordGenericError: {
    id: "resendPasswordGenericError",
    defaultMessage: "Something went wrong. Please try again.",
  },
});

const ResetPassword: FC = () => {
  const { config } = React.useContext(ConfigContext);

  const {
    resetPassword,
    resetPasswordIsLoading,
    resetPasswordData,
    resetPasswordError,
    clearResetPasswordError,
  } = useContext(ResetPasswordContext);

  const {
    acquireTokens,
    handleRecaptchaError,
    promoteToV2Challenge,
    resetV2Challenge,
    v2Challenge,
    RecaptchaV3UnavailableError,
  } = useRecaptchaSubmit({
    action: RECAPTCHA_RESET_PASSWORD_ACTION,
    v2ContainerClassName: "reset-password__recaptcha-v2",
    v2TestId: "reset-password-recaptcha-v2",
  });

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const [email, setEmail] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const intl = useIntl();
  const navigate = useNavigate();
  const errorController = useErrorController();

  const isLoading = resetPasswordIsLoading || localLoading;

  useEffect(() => {
    if (resetPasswordData?.email && email && !isLoading) {
      resetV2Challenge();
      navigate(routes.sendEmail, { replace: true });
    }
  }, [resetPasswordData, isLoading]);

  const submitResetPassword = async (
    emailValue: string,
    tokensOverride?: RecaptchaTokens
  ) => {
    setLocalLoading(true);
    let captchaTokens: RecaptchaTokens;
    try {
      captchaTokens = tokensOverride ?? (await acquireTokens());
    } catch (err) {
      setLocalLoading(false);
      if (err instanceof RecaptchaV3UnavailableError) {
        promoteToV2Challenge(err.reason, async (tokens) => {
          await submitResetPassword(emailValue, tokens);
        });
        return;
      }
      throw err;
    }

    try {
      await resetPassword(emailValue, captchaTokens);
      resetV2Challenge();
    } catch (err) {
      const handled = handleRecaptchaError(err, async (tokens) => {
        await submitResetPassword(emailValue, tokens);
      });
      if (!handled) {
        throw err;
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const handleSubmit = (emailValue: string) => {
    clearResetPasswordError();
    errorController.clearValidationError(emailValidationFieldName);

    if (!validateEmail(emailValue, usePlusSignInEmailAddress)) {
      errorController.setValidationError(
        emailValidationFieldName,
        intl.formatMessage(messages.resendPasswordInvalidEmailError)
      );
    } else {
      void submitResetPassword(emailValue);
    }
  };

  const handleChange = (emailValue: string) => {
    clearResetPasswordError();
    errorController.clearValidationError(emailValidationFieldName);
    setEmail(emailValue);
  };

  const title: ReactElement = (
    <section className="title__container">
      <h1>{intl.formatMessage(messages.resendPasswordTitle)}</h1>
      <p>{intl.formatMessage(messages.resendPasswordInstructions)}</p>
    </section>
  );

  const genericErrorNotification: ReactElement | undefined =
    resetPasswordError && !isRecaptchaErrorCode(resetPasswordError) ? (
      <Notification type="error" role="alert">
        {intl.formatMessage(messages.resendPasswordGenericError)}
      </Notification>
    ) : undefined;

  const resetPasswordForm: ReactElement = (
    <section className="form__container">
      <ErrorProvider controller={errorController}>
        <ResetPasswordForm
          onSubmit={handleSubmit}
          onChangeEmail={handleChange}
        />
      </ErrorProvider>
      {v2Challenge}
    </section>
  );

  return (
    <article className="reset-password__container">
      <SpinnerView isLoading={isLoading} />
      {title}
      {genericErrorNotification}
      {resetPasswordForm}
    </article>
  );
};

export default ResetPassword;
